import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './SystemDashboard.css';
import { useAuth } from '../../hooks/useAuth';
import { ArrowLeft, LogOut, LayoutDashboard, Users, MessageSquare, Stethoscope } from 'lucide-react';
import OverviewTab from './components/OverviewTab';
import UsersTab from './components/UsersTab';
import SocialTab from './components/SocialTab';
import MedicalTab from './components/MedicalTab';
import DashboardFilters from './components/DashboardFilters';
import { DEFAULT_FILTERS } from './constants/filters';

import DetailedOverviewPage from './pages/DetailedOverviewPage';
import DetailedUsersPage from './pages/DetailedUsersPage';
import DetailedSocialPage from './pages/DetailedSocialPage';
import DetailedMedicalPage from './pages/DetailedMedicalPage';

const SystemDashboard = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/auth/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const activeTab = searchParams.get('tab') || 'overview';
    const viewMode = searchParams.get('view') || 'summary';

    const filters = {
        timeRange: searchParams.get('timeRange') || DEFAULT_FILTERS.timeRange,
        category: searchParams.get('category') || DEFAULT_FILTERS.category,
        start_date: searchParams.get('start_date') || DEFAULT_FILTERS.start_date,
        end_date: searchParams.get('end_date') || DEFAULT_FILTERS.end_date,
        gender: searchParams.get('gender') || DEFAULT_FILTERS.gender,
        min_age: searchParams.get('min_age') || DEFAULT_FILTERS.min_age,
        max_age: searchParams.get('max_age') || DEFAULT_FILTERS.max_age,
        min_reactions: searchParams.get('min_reactions') || DEFAULT_FILTERS.min_reactions,
        min_comments: searchParams.get('min_comments') || DEFAULT_FILTERS.min_comments,
        visit_type: searchParams.get('visit_type') || DEFAULT_FILTERS.visit_type,
        country_id: searchParams.get('country_id') || DEFAULT_FILTERS.country_id
    };

    const categoryConfig = {
        users: {
            show: true,
            label: 'Type',
            options: [
                { value: 'PET_GUARDIAN', label: 'Pet Guardian' },
                { value: 'VETERINARIAN', label: 'Vet' },
                { value: 'SERVICE_PROVIDER', label: 'Service Provider' },
                { value: 'FIRM', label: 'Firm' }
            ]
        },
        social: {
            show: true,
            label: 'Type',
            options: [
                { value: 'post', label: 'Post' },
                { value: 'poll', label: 'Poll' },
                { value: 'adoption', label: 'Adoption' }
            ]
        },
        medical: {
            show: true,
            label: 'Status',
            options: [
                { value: 'pending', label: 'Pending' },
                { value: 'confirmed', label: 'Confirmed' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' }
            ]
        },
        overview: {
            show: false
        }
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: LayoutDashboard },
        { id: 'users', label: 'Users', icon: Users },
        { id: 'social', label: 'Social', icon: MessageSquare },
        { id: 'medical', label: 'Medical', icon: Stethoscope }
    ];

    const handleFilterChange = (newFilters) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(newFilters).forEach(([key, value]) => {
            // Keep 'all' for timeRange (it is a distinct state from the default 30days),
            // but treat category='all' as the default and remove it from the URL.
            const shouldKeep = value && (key !== 'category' || value !== 'all');
            if (shouldKeep) {
                newParams.set(key, value);
            } else {
                newParams.delete(key);
            }
        });
        setSearchParams(newParams);
    };

    const setActiveTab = (tab) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', tab);
        newParams.set('view', 'summary');
        newParams.set('category', 'all');

        ['gender', 'min_age', 'max_age', 'country_id', 'min_reactions', 'min_comments', 'visit_type'].forEach(
            (key) => newParams.delete(key)
        );

        setSearchParams(newParams);
    };

    const setViewMode = (view) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('view', view);
        setSearchParams(newParams);
    };

    const ActiveIcon = tabs.find((t) => t.id === activeTab)?.icon || LayoutDashboard;

    return (
        <div className="dashboard-container">
            <div className="dashboard-content">
                {/* Top nav */}
                <div className="dashboard-nav-bar">
                    <button
                        className="dashboard-nav-btn dashboard-nav-btn--back"
                        onClick={() => navigate('/admin/dashboard')}
                        title="Back to Admin Dashboard"
                    >
                        <ArrowLeft size={16} />
                        <span>Admin Dashboard</span>
                    </button>
                    <button
                        className="dashboard-nav-btn dashboard-nav-btn--logout"
                        onClick={handleLogout}
                        title="Logout"
                    >
                        <LogOut size={16} />
                        <span>Logout</span>
                    </button>
                </div>

                {/* Header */}
                {viewMode === 'summary' ? (
                    <div className="dashboard-header sd-header">
                        <h1 className="header-title">System Statistics</h1>
                        <p className="header-subtitle">Real-time analytics and insights</p>
                    </div>
                ) : (
                    <div className="dashboard-header-mini sd-header">
                        <div className="sd-detailed-header__left">
                            <button
                                onClick={() => setViewMode('summary')}
                                className="sd-detailed-header__back"
                                title="Back to Dashboard"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <ActiveIcon size={22} className="sd-header__icon" />
                            <div>
                                <h1 className="sd-detailed-header__title">
                                    {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Analytics
                                </h1>
                                <p className="sd-detailed-header__subtitle">Detailed statistics and insights</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabs + Filters row */}
                {viewMode === 'summary' && (
                    <div className="dashboard-toolbar">
                        <div className="tabs-section">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                                    >
                                        <Icon size={16} />
                                        <span>{tab.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <DashboardFilters
                            currentFilters={filters}
                            onFilterChange={handleFilterChange}
                            showCategoryFilter={categoryConfig[activeTab]?.show}
                            categoryLabel={categoryConfig[activeTab]?.label}
                            categories={categoryConfig[activeTab]?.options}
                            activeTab={activeTab}
                        />
                    </div>
                )}

                {viewMode === 'detailed' && (
                    <div className="sd-detailed-filters">
                        <DashboardFilters
                            currentFilters={filters}
                            onFilterChange={handleFilterChange}
                            showCategoryFilter={categoryConfig[activeTab]?.show}
                            categoryLabel={categoryConfig[activeTab]?.label}
                            categories={categoryConfig[activeTab]?.options}
                            activeTab={activeTab}
                        />
                    </div>
                )}

                {/* Content Area */}
                <div className="tab-content">
                    {viewMode === 'summary' ? (
                        <>
                            {activeTab === 'overview' && (
                                <OverviewTab
                                    filters={filters}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                            {activeTab === 'users' && (
                                <UsersTab
                                    filters={filters}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                            {activeTab === 'social' && (
                                <SocialTab
                                    filters={filters}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                            {activeTab === 'medical' && (
                                <MedicalTab
                                    filters={filters}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            {activeTab === 'overview' && (
                                <DetailedOverviewPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                            {activeTab === 'users' && (
                                <DetailedUsersPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                            {activeTab === 'social' && (
                                <DetailedSocialPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                            {activeTab === 'medical' && (
                                <DetailedMedicalPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SystemDashboard;
